/**
 * QR 출력 - 전화번호로 회원/자녀 조회 후 QR 스티커 출력
 * 화면 숫자패드로 전화번호 입력 (안드로이드 키보드 미사용)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMemberByPhone, getFamilyMembers } from '../services/api';
import StoreBadge from '../components/StoreBadge';
import './QrPrintPage.css';

import { isBridgeApp } from '../utils/isBridgeApp';
const NUMPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['clear', '0', 'backspace'],
];

const QrPrintPage = () => {
  const navigate = useNavigate();
  const printRef = useRef(null);

  const [digits, setDigits] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 가족 구성원 데이터
  const [familyData, setFamilyData] = useState(null); // { parent, children }
  const [selected, setSelected] = useState(new Set()); // 선택된 member_id Set
  const [showFsOverlay, setShowFsOverlay] = useState(false); // 전체화면 복귀 오버레이
  const wasFullscreen = useRef(false);

  // 인쇄 후 전체화면 복귀 처리
  useEffect(() => {
    const handleBeforePrint = () => {
      wasFullscreen.current = !!(document.fullscreenElement || document.webkitFullscreenElement);
    };
    const handleAfterPrint = () => {
      if (wasFullscreen.current) {
        setShowFsOverlay(true);
      }
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  const restoreFullscreen = useCallback(() => {
    setShowFsOverlay(false);
    // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
    if (!isBridgeApp()) return;
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  }, []);

  // 숫자 → 포맷 (010-XXXX-XXXX) - digits는 뒤 8자리만 저장
  const formatPhone = (d) => {
    if (d.length === 0) return '';
    if (d.length <= 4) return `010-${d}`;
    return `010-${d.slice(0, 4)}-${d.slice(4)}`;
  };

  const displayPhone = digits.length > 0 ? formatPhone(digits) : '';

  // 숫자패드 입력
  const handleKeyPress = (key) => {
    setError(null);
    if (key === 'backspace') {
      setDigits((prev) => prev.slice(0, -1));
    } else if (key === 'clear') {
      setDigits('');
    } else {
      setDigits((prev) => (prev.length < 8 ? prev + key : prev));
    }
  };

  // 전체 회원 목록 (부모 + 자녀)
  const getAllMembers = (data) => {
    if (!data) return [];
    const members = [data.parent];
    if (data.children) {
      members.push(...data.children);
    }
    return members;
  };

  // 회원 조회 → 가족 조회
  const handleSearch = async () => {
    if (digits.length !== 8) {
      setError('전화번호 뒤 8자리를 입력해주세요');
      return;
    }

    setLoading(true);
    setError(null);
    setFamilyData(null);
    setSelected(new Set());

    try {
      // 1. 전화번호로 회원 조회
      const memberRes = await getMemberByPhone('010' + digits);
      const member = memberRes.member;

      // 2. 가족 구성원 조회 (QR 이미지 포함)
      const familyRes = await getFamilyMembers(member.member_id, true);

      setFamilyData({
        parent: { ...familyRes.parent },
        children: familyRes.children || [],
      });

      // 기본: 모두 선택
      const allIds = new Set();
      allIds.add(familyRes.parent.member_id);
      (familyRes.children || []).forEach((c) => allIds.add(c.member_id));
      setSelected(allIds);
    } catch (err) {
      if (err.status === 404 || err.error_code === 'MEMBER_NOT_FOUND') {
        setError('등록되지 않은 전화번호입니다');
      } else {
        setError(err.message || '회원 조회에 실패했습니다');
      }
    } finally {
      setLoading(false);
    }
  };

  // 선택 토글
  const toggleSelect = (memberId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  // 모두 선택
  const selectAll = () => {
    const allIds = new Set();
    getAllMembers(familyData).forEach((m) => allIds.add(m.member_id));
    setSelected(allIds);
  };

  // 모두 해제
  const deselectAll = () => {
    setSelected(new Set());
  };

  // QR 스티커 인쇄
  const handlePrint = () => {
    if (selected.size === 0) return;
    window.print();
  };

  // 초기화
  const handleReset = () => {
    setDigits('');
    setFamilyData(null);
    setSelected(new Set());
    setError(null);
  };

  const maskPhone = (p) => {
    if (!p || p.length < 8) return p || '-';
    return p.slice(0, 3) + '-****-' + p.slice(-4);
  };

  const getMemberLabel = (m) => {
    if (m.member_type === 'CHILD') {
      return m.nickname || m.name || '자녀';
    }
    return m.name || m.nickname || '보호자';
  };

  const allMembers = getAllMembers(familyData);
  const selectedMembers = allMembers.filter((m) => selected.has(m.member_id));

  return (
    <div className="qrprint-page">
      {/* 헤더 */}
      <header className="qrp-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          ← 메뉴
        </button>
        <div className="qrp-header-center" onClick={() => {
          // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
          if (!isBridgeApp()) return;
          const el = document.documentElement;
          (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
        }}>
          <h1>🏷️ QR 출력</h1>
          <StoreBadge />
        </div>
        <div className="qrp-header-spacer" />
      </header>

      <main className="qrp-content">
        {/* 전화번호 입력 (숫자패드) */}
        {!familyData && !loading && (
          <div className="qrp-search-section">
            <p className="qrp-desc">전화번호를 입력하면<br/>가족 QR 스티커를 출력합니다</p>

            <div className="qrp-phone-display">
              <span className={`qrp-phone-text ${digits.length === 0 ? 'placeholder' : ''}`}>
                <span style={{color: '#999'}}>010</span>
                {digits.length > 0 ? `-${digits.slice(0, 4)}` : '-'}
                {digits.length > 4 ? `-${digits.slice(4)}` : ''}
                {digits.length === 0 && <span className="placeholder"> 뒤 8자리 입력</span>}
              </span>
            </div>

            <div className="qrp-numpad">
              {NUMPAD_KEYS.map((row, ri) => (
                <div className="qrp-numpad-row" key={ri}>
                  {row.map((key) => (
                    <button
                      key={key}
                      className={`qrp-numpad-key ${key === 'clear' ? 'key-clear' : ''} ${key === 'backspace' ? 'key-backspace' : ''}`}
                      onClick={() => handleKeyPress(key)}
                    >
                      {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                    </button>
                  ))}
                </div>
              ))}
              <button
                className="btn-search"
                onClick={handleSearch}
                disabled={digits.length !== 8}
              >
                조회
              </button>
            </div>

            {error && (
              <div className="qrp-error">
                <span>⚠️</span> {error}
              </div>
            )}
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="qrp-loading">
            <div className="qrp-spinner" />
            <p>회원 조회 중...</p>
          </div>
        )}

        {/* 가족 구성원 목록 + QR 선택 */}
        {familyData && !loading && (
          <div className="qrp-result-section">
            {/* 전화번호 표시 */}
            <div className="qrp-phone-banner">
              {maskPhone(familyData.parent.phone)} · {allMembers.length}명
            </div>

            {/* 선택 컨트롤 */}
            <div className="qrp-select-controls">
              <button
                className={`btn-select-ctrl ${selected.size === allMembers.length ? 'active' : ''}`}
                onClick={selectAll}
              >
                모두선택
              </button>
              <button
                className={`btn-select-ctrl ${selected.size === 0 ? 'active' : ''}`}
                onClick={deselectAll}
              >
                모두해제
              </button>
              <span className="qrp-select-count">{selected.size}명 선택</span>
            </div>

            {/* 회원 카드 리스트 */}
            <div className="qrp-member-list">
              {allMembers.map((m) => (
                <div
                  key={m.member_id}
                  className={`qrp-member-item ${selected.has(m.member_id) ? 'selected' : ''}`}
                  onClick={() => toggleSelect(m.member_id)}
                >
                  <div className="qrp-member-check">
                    {selected.has(m.member_id) ? '✅' : '⬜'}
                  </div>
                  <div className="qrp-member-qr">
                    {m.qr_image_base64 ? (
                      <img src={m.qr_image_base64} alt="QR" />
                    ) : (
                      <div className="qrp-no-qr">QR없음</div>
                    )}
                  </div>
                  <div className="qrp-member-info">
                    <span className="qrp-member-label">
                      {m.member_type === 'CHILD' ? '👶' : '👤'} {getMemberLabel(m)}
                    </span>
                    <span className="qrp-member-type">
                      {m.member_type === 'CHILD' ? '자녀' : m.member_type === 'PARENT' ? '보호자' : '일반'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* 액션 버튼 */}
            <div className="qrp-action-buttons">
              <button
                className="btn-print"
                onClick={handlePrint}
                disabled={selected.size === 0}
              >
                🖨️ QR 출력 ({selected.size}명)
              </button>
              <button className="btn-reset" onClick={handleReset}>
                ← 다른 회원 조회
              </button>
            </div>
          </div>
        )}
      </main>

      {/* 전체화면 복귀 오버레이 */}
      {showFsOverlay && (
        <div className="qrp-fs-overlay" onClick={restoreFullscreen}>
          <div className="qrp-fs-popup">
            <div className="qrp-fs-icon">📺</div>
            <h3>전체화면이 해제되었습니다</h3>
            <p>화면을 터치하면 전체화면으로 돌아갑니다</p>
          </div>
        </div>
      )}

      {/* 인쇄 전용 영역 (화면에는 안 보임, print 시에만 보임) */}
      <div className="qrp-print-area" ref={printRef}>
        {selectedMembers.map((m) => (
          <div key={m.member_id} className="qrp-sticker">
            {m.qr_image_base64 && (
              <img src={m.qr_image_base64} alt="QR" className="qrp-sticker-qr" />
            )}
            <div className="qrp-sticker-name">{getMemberLabel(m)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QrPrintPage;
